import React from "react";
import { GsdDemoModeBanner } from "./GsdDemoModeBanner.jsx";
import { GsdTopNavGroups } from "./GsdRoleTabs.jsx";
import { GsdMobileNav } from "./GsdMobileNav.jsx";
import { GsdRoleBadge } from "./GsdRoleSwitcher.jsx";
import { getLocaleLabel } from "../config/resolveNavigation.js";

/**
 * @param {object} props
 * @param {import('../config/appConfigTypes.js').GsdAppConfig} props.config
 * @param {string} [props.locale]
 * @param {string} props.activeRole
 * @param {boolean} [props.isAuthenticated]
 * @param {string} [props.userDisplayName]
 * @param {string} [props.version]
 * @param {React.ReactNode} [props.brand]
 * @param {string} [props.brandHref]
 * @param {React.ReactNode} [props.languageToggle]
 * @param {React.ReactNode} [props.helpSlot]
 * @param {React.ReactNode} [props.trainingSlot]
 * @param {() => void} [props.onLogout]
 * @param {React.ReactNode} [props.loginSlot]
 * @param {Array} props.navGroups
 * @param {(item: object, meta: object) => React.ReactNode} [props.renderNavLink]
 * @param {(item: object) => boolean} [props.isItemActive]
 * @param {boolean} [props.mobileNavOpen]
 * @param {() => void} [props.onMobileNavToggle]
 * @param {'top-nav'|'sidebar'} [props.layoutVariant]
 * @param {boolean} [props.hideHeaderNav]
 * @param {React.ReactNode} [props.userContextSlot]
 * @param {React.ReactNode} [props.roleSwitcherSlot]
 * @param {Array} [props.mobileNavItems]
 * @param {string} [props.testId]
 */
export function GsdTopHeader({
  config,
  locale = "fr",
  activeRole,
  isAuthenticated = false,
  userDisplayName,
  version,
  brand,
  brandHref = "/",
  languageToggle,
  helpSlot,
  trainingSlot,
  onLogout,
  loginSlot,
  navGroups = [],
  renderNavLink,
  isItemActive,
  mobileNavOpen = false,
  onMobileNavToggle,
  layoutVariant = "top-nav",
  hideHeaderNav = false,
  userContextSlot,
  roleSwitcherSlot,
  mobileNavItems,
  testId = "gsd-top-header",
}) {
  const phaseLabel = getLocaleLabel(
    { labelFr: config.phaseLabel?.fr, labelEn: config.phaseLabel?.en },
    locale,
  );
  const demoMessage = getLocaleLabel(
    { labelFr: config.demoBanner?.fr, labelEn: config.demoBanner?.en },
    locale,
  );
  const activeRoleMeta = (config.roles || []).find((r) => r.id === activeRole);
  const roleForBadge = activeRoleMeta
    ? { ...activeRoleMeta, label: getLocaleLabel(activeRoleMeta, locale) }
    : { id: activeRole, label: activeRole, icon: "" };

  return (
    <header className="gsd-top-header" data-testid={testId}>
      <div className="gsd-top-header__accent" aria-hidden="true" />
      <div className="gsd-top-header__inner">
        {brand ? (
          brand
        ) : (
          <a href={brandHref} className="gsd-top-header__brand" data-testid="gsd-brand-link">
            <span className="gsd-top-header__brand-main">{config.appShortName || config.appName}</span>
            <span className="gsd-top-header__brand-sub">{config.appName}</span>
          </a>
        )}

        <button
          type="button"
          className="gsd-top-header__mobile-toggle"
          aria-expanded={mobileNavOpen}
          aria-controls="gsd-mobile-nav-panel"
          onClick={onMobileNavToggle}
          data-testid="gsd-mobile-nav-toggle"
        >
          Menu
        </button>

        {layoutVariant === "top-nav" && !hideHeaderNav ? (
          <GsdTopNavGroups
            groups={navGroups}
            renderLink={renderNavLink}
            isItemActive={isItemActive}
            className={mobileNavOpen ? "gsd-top-nav--open" : ""}
            testId="gsd-main-nav"
          />
        ) : null}

        <div className="gsd-top-header__actions" data-testid="gsd-header-actions">
          {userContextSlot}
          {roleSwitcherSlot}
          {languageToggle}
          {helpSlot}
          {trainingSlot}
          {isAuthenticated && activeRole !== "guest" ? (
            <GsdRoleBadge role={roleForBadge} testId="gsd-header-role-badge" />
          ) : null}
          {isAuthenticated ? (
            <>
              {userDisplayName ? (
                <span className="gsd-top-header__user" data-testid="gsd-user-chip">
                  {userDisplayName}
                </span>
              ) : null}
              {onLogout ? (
                <button type="button" className="gsd-top-header__logout" onClick={onLogout} data-testid="gsd-logout-btn">
                  {locale === "en" ? "Log out" : "Déconnexion"}
                </button>
              ) : null}
            </>
          ) : (
            loginSlot
          )}
        </div>
      </div>

      <GsdDemoModeBanner
        mode={config.mode || "production"}
        phaseLabel={phaseLabel}
        demoMessage={demoMessage}
        version={version}
      />

      <GsdMobileNav
        open={mobileNavOpen}
        onClose={() => onMobileNavToggle?.()}
        groups={navGroups}
        flatItems={mobileNavItems}
        renderLink={renderNavLink}
        isItemActive={isItemActive}
        activeRole={isAuthenticated ? roleForBadge : null}
        helpSlot={helpSlot}
        trainingSlot={trainingSlot}
      />
    </header>
  );
}
